import { z } from 'zod/v4';
import { AssignmentPrioritySchema } from '../assignments/assignment.schema.js';
export const OrderStatusSchema = z.enum([
    'draft',
    'submitted',
    'in-progress',
    'pending-review',
    'accepted',
    'rejected',
    'completed',
    'cancelled',
    'submission-in-doubt',
    'escalated',
    'ready-for-review',
    'partially-accepted',
]);
export const OrderTypeSchema = z.enum([
    'e-filing',
    'court-filing',
    'process-serving',
    'eservice',
    'delivery',
    'skip-trace',
    'concierge',
    'document-retrieval',
    'courtesy-copy-delivery',
    'secretary-of-state-filing',
    'county-recording',
]);
// Physical Order Type (for dispatched orders)
export const PhysicalOrderTypeSchema = z.enum([
    'court-filing',
    'courtesy-copy-delivery',
    'records-retrieval',
    'skip-tracing',
]);
// Account Type
export const AccountTypeSchema = z.enum([
    'concierge',
    'direct',
]);
// Order Source
export const OrderSourceSchema = z.enum([
    'portal',
    'concierge',
    'api',
]);
// Hold Reason
export const HoldReasonSchema = z.enum([
    'awaiting-court-response',
    'multi-step-process',
    'partially-accepted',
]);
// eFiling-specific enums
export const EFilingTypeSchema = z.enum([
    'case-initiation',
    'subsequent-filing',
]);
// eFiling fee breakdown
export const EFilingFeesSchema = z.object({
    courtFees: z.number().default(0),
    convenienceFee: z.number().default(0),
    courtTransactionFee: z.number().default(0),
    totalCourtFees: z.number().default(0),
    serviceFee: z.number().default(0),
    processingFee: z.number().default(0),
    totalFees: z.number().default(0),
});
export const OrderSchema = z.object({
    id: z.string().uuid(),
    type: OrderTypeSchema,
    status: OrderStatusSchema,
    caseNumber: z.string().optional(),
    caseTitle: z.string().optional(),
    court: z.string(),
    courtLocation: z.string().optional(),
    documents: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        url: z.string().url().optional(),
        type: z.string(),
        size: z.number(),
    })).optional(),
    notes: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    submittedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    userId: z.string().uuid(),
    totalAmount: z.number().optional(),
    referenceNumber: z.string().optional(),
    accountType: AccountTypeSchema.optional(),
    priority: AssignmentPrioritySchema.default('standard'),
    physicalOrderType: PhysicalOrderTypeSchema.optional(),
    holdReason: HoldReasonSchema.optional(),
    isOnHold: z.boolean().optional(),
    // eFiling-specific fields
    state: z.string().optional(),
    county: z.string().optional(),
    filingType: EFilingTypeSchema.optional(),
    isFederal: z.boolean().optional(),
    serveAfterFiling: z.boolean().optional(),
    eServeCaseParticipants: z.boolean().optional(),
    messageToClerk: z.string().optional(),
    internalReferenceNumber: z.string().optional(),
    efilingFees: EFilingFeesSchema.optional(),
    authorizeCourtFeePayment: z.boolean().optional(),
});
export const CreateOrderSchema = OrderSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    submittedAt: true,
    completedAt: true,
    referenceNumber: true,
});
export const UpdateOrderSchema = OrderSchema.partial().omit({
    id: true,
    createdAt: true,
    userId: true,
});
// ---------------------------------------------------------------------------
// New: Base + typeData schemas (Phase 1 — additive, no breaking changes)
// Consumers will migrate from OrderSchema to TypedOrderSchema in Phase 2
// ---------------------------------------------------------------------------
import { OrderTypeDataSchema } from './order-types.schema.js';
import { OrderDocumentSchema } from './document.schema.js';
import { FeeWaiverBasisRecordSchema } from '../efiling/order-logic/common/fee-waiver-basis.js';
/** Shared fields common to all order types */
export const OrderBaseSchema = z.object({
    id: z.string().uuid(),
    type: OrderTypeSchema,
    status: OrderStatusSchema,
    caseNumber: z.string().optional(),
    caseTitle: z.string().optional(),
    court: z.string().optional(),
    courtLocation: z.string().optional(),
    notes: z.string().optional(),
    internalReferenceNumber: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    submittedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    userId: z.string().uuid(),
    totalAmount: z.number().optional(),
    referenceNumber: z.string().optional(),
    accountType: AccountTypeSchema.optional(),
    priority: AssignmentPrioritySchema.default('standard'),
    physicalOrderType: PhysicalOrderTypeSchema.optional(),
    holdReason: HoldReasonSchema.optional(),
    isOnHold: z.boolean().optional(),
    source: OrderSourceSchema.optional(),
    refiledFromOrderId: z.string().uuid().optional(),
    // Fee-waiver basis record (SOR-237): present exactly when the charge seam
    // suppressed the order's LDS fees under a fee waiver. Optional-additive —
    // pre-migration rows carry none and every existing parse is unchanged.
    feeWaiverBasis: FeeWaiverBasisRecordSchema.optional(),
    // Per-order legal version stamp (SOR-377): the integer registry version of the
    // Terms of Service and of the Privacy Policy in force at the submit instant,
    // written at the submit seam. Optional-additive — a draft, or a row predating
    // the columns, carries neither and every existing parse is unchanged.
    termsVersionAtSubmission: z.number().int().optional(),
    privacyVersionAtSubmission: z.number().int().optional(),
});
/** Order with type-discriminated typeData (replaces flat OrderSchema in Phase 2) */
export const TypedOrderSchema = OrderBaseSchema.extend({
    typeData: OrderTypeDataSchema,
    documents: z.array(OrderDocumentSchema).optional(),
    // SOR-375 write-time report: the fileNames a create/update/refile dropped
    // from `type_data.documents[]` because no `order_documents` row with storage
    // behind them backs the entry. Response-only — never persisted on the order,
    // and absent whenever the write dropped nothing.
    rejectedDocuments: z.array(z.string()).optional(),
});
/** Loose typeData for create/update — must include orderType discriminator, rest is freeform.
 *  Full validation happens at submit time via validateOrderTypeData(). */
const LooseTypeDataSchema = z.record(z.string(), z.unknown()).refine((v) => typeof v.orderType === 'string', { message: 'typeData.orderType is required' });
export const CreateTypedOrderSchema = OrderBaseSchema.omit({
    id: true,
    userId: true,
    createdAt: true,
    updatedAt: true,
    submittedAt: true,
    completedAt: true,
    referenceNumber: true,
}).extend({
    status: OrderStatusSchema.default('draft'),
    typeData: LooseTypeDataSchema,
    documents: z.array(OrderDocumentSchema).optional(),
});
/**
 * The order fields the SERVER owns on an update. A customer PATCH — and the
 * `orders.update` MCP tool, which parses with the same schema — may never set
 * one: `status` is moved by the lifecycle, `totalAmount`/`feeWaiverBasis` by the
 * submit + charge seams, the timestamps/reference by the court round-trip, and
 * `source`/`refiledFromOrderId`/`isOnHold`/`holdReason` by the server-side
 * writers that minted them.
 *
 * `UpdateTypedOrderSchema` omits every one of these AND rejects a body that
 * names one (`.strict()`), rather than stripping it the way a plain Zod object
 * would — a silent strip answers `200` to `{status:'submitted'}`, so a client
 * bug or an MCP agent probing the surface would get a success for a write that
 * never happened. Rejecting names the offending field instead.
 */
export const SERVER_OWNED_ORDER_UPDATE_FIELDS = [
    'status',
    'totalAmount',
    'submittedAt',
    'completedAt',
    'referenceNumber',
    'feeWaiverBasis',
    'termsVersionAtSubmission',
    'privacyVersionAtSubmission',
    'source',
    'refiledFromOrderId',
    'isOnHold',
    'holdReason',
    'updatedAt',
];
export const UpdateTypedOrderSchema = OrderBaseSchema.partial().omit({
    id: true,
    createdAt: true,
    userId: true,
    status: true,
    totalAmount: true,
    submittedAt: true,
    completedAt: true,
    referenceNumber: true,
    feeWaiverBasis: true,
    termsVersionAtSubmission: true,
    privacyVersionAtSubmission: true,
    source: true,
    refiledFromOrderId: true,
    isOnHold: true,
    holdReason: true,
    updatedAt: true,
}).extend({
    typeData: LooseTypeDataSchema.optional(),
    documents: z.array(OrderDocumentSchema).optional(),
}).strict();
